<template>
    <div v-if="installation">
        <vrm-header :installation="installation">
            <template v-slot:header-title>
                {{ installation.name }}
            </template>
        </vrm-header>
        <vrm-title>
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    {{
                        $t('dynamicess.title', {
                            i18nFallback: 'Dynamic ESS for {name}',
                            name: installation.name,
                        })
                    }}
                </h1>
            </template>
            <template v-slot:mobile-page-title>
                <h1 class="vrm-page-title">
                    {{ $t('dynamicess.dynamicEss', { i18nFallback: 'Dynamic ESS' }) }}
                </h1>
            </template>
        </vrm-title>
        <div class="container">
            <div
                v-html="
                    $t('dynamicess.description', {
                        i18nFallback:
                            'Dynamic ESS aims to minimise the costs for people with variable energy contracts by controlling the battery usage.',
                    })
                "
            ></div>
            <br v-if="!userCanEdit" />
            <div
                v-if="!userCanEdit"
                v-html="
                    $t('dynamicess.accessRemark', {
                        i18nFallback:
                            'Only users with full control can change the dynamic ess settings. Please contact the owner of the installation to change the settings.',
                    })
                "
            ></div>
            <br />
            <form class="vrm-form vrm-form__dynamic-ess">
                <span v-if="apiDynamicEssToggle === null" class="spinner_container">
                    <spinner />
                </span>
                <span v-else-if="apiDynamicEssToggle && !showWizard" class="fragment">
                    <confirmation
                        :user-can-edit="userCanEdit"
                        :with-toggle="true"
                        :toggle="dynamicEssToggle"
                        @edit="editStep"
                        @toggle="toggleDynamicEss"
                    />
                </span>
                <span v-else class="fragment">
                    <closure v-if="closure" />
                    <landing
                        v-if="!closure && !showWizard"
                        :user-can-edit="userCanEdit"
                        :is-configuration-empty="isConfigurationEmpty"
                        @config="startConfig"
                    />
                    <br />
                    <wizard
                        v-if="!closure && showWizard"
                        :value="currentId"
                        :steps="getSteps"
                        :is-edit="isEdit"
                        :completed-until="completedUntil"
                        :can-submit="apiDynamicEssToggle && isSellValid"
                        :next-disabled="isStepValid()"
                        :has-change="hasChange"
                        :creating="creating"
                        @input="setCurrentId"
                        @reset="resetWizard"
                        @submit="onSubmit"
                        @submitAndReset="submitAndReset"
                        @resetForm="onResetForm"
                    >
                        <grid
                            v-if="currentId == STEPS.GRID"
                            :initial-values="initialForm"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                        <battery
                            v-if="currentId == STEPS.BATTERY"
                            :initial-values="initialForm"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                        <buy-form
                            v-if="currentId == STEPS.ENERGY_PRICES_BUY"
                            :initial-values="initialForm"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                        <sell-form
                            v-if="currentId == STEPS.ENERGY_PRICES_SELL"
                            :initial-values="initialForm"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                        <confirmation v-if="currentId == STEPS.CONFIRMATION" @edit="editStep" />
                    </wizard>
                </span>
            </form>
            <div v-if="!closure && showWizard" class="vrm-info-box dynamic-ess__community">
                <span class="sg-vrm-icon_061_info-round-16px" />
                <span v-html="$t('dynamicess.troubles', { i18nFallback: troublesFallback })"></span>
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmHeader from '@/vue/components/layout/VrmHeader';
import VrmTitle from '@/vue/components/layout/VrmTitle';
import Wizard from './wizard/Wizard.vue';
import Landing from './Landing.vue';
import Closure from './Closure.vue';
import Grid from './forms/Grid.vue';
import Battery from './forms/Battery.vue';
import BuyForm from './forms/BuyForm.vue';
import Confirmation from './forms/Confirmation.vue';
import SellForm from './forms/SellForm.vue';
import Spinner from '@/vue/components/shared/device-controls/controls/components/Spinner';
import {
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_WARNING,
    NOTIFICATION_TYPE_INFO,
} from '@/vue/store/modules/notifications';
import { applyData, createForm, DYNAMIC_ESS } from '@/vue/store/modules/dynamic-ess-form.js';
import { STEPS } from '@/vue/components/pages/dynamic-ess/constants.js';
import { CURRENCIES } from '@/vue/store/modules/currencies';

const checkIfPresent = value => {
    return value !== null && value !== undefined;
};

@Component({
    components: {
        Wizard,
        Landing,
        Grid,
        Battery,
        BuyForm,
        SellForm,
        Confirmation,
        VrmHeader,
        Closure,
        Spinner,
        VrmTitle,
    },
})
@AngularRoute('base.installation.settings.dynamicEss', '/dynamic-ess', { slot: 'settings' })
@AngularLocaleBundle('dynamicess')
@AngularLocaleBundle('ess')
export default class DynamicEss extends Vue {
    @ngInject()
    ApiService;
    @ngInject()
    InstallationsService;
    @ngInject()
    localStorageService;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    MQTTRealtimeService;
    @ngInject()
    $rootScope;

    currentId = STEPS.GRID;
    STEPS = STEPS;
    showWizard = false;
    isEdit = false;
    dynamicEssToggle = false;
    apiDynamicEssToggle = null;
    installation = null;
    userCanEdit = false;
    realtimeDessMode = null;
    hasChange = false;
    creating = false;
    formChanges = {
        grid: false,
        battery: false,
        buyForm: false,
        sellForm: false,
    };

    hasVenusConfigSupport = false;

    troublesFallback =
        "Having difficulties? Please visit the <a href='https://community.victronenergy.com/t/about-the-dess-category/1589' target='_blank'> community thread </a> to report your issue.";

    closure = false;
    initialForm = createForm();

    async toggleDynamicEss(value) {
        const valid = this.$store.getters[DYNAMIC_ESS.FORM_VALID];
        if (!valid) {
            if (value) {
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_SUCCESS,
                    message: this.$t('dynamicess.continueConfig'),
                });
            }

            // I hate vrm angular bridge, todo: look into this?
            setTimeout(() => {
                this.dynamicEssToggle = value;
            });

            return;
        }

        // Form is valid, can toggle on/off
        if (!value) {
            if (this.realtimeDessMode === null) {
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_WARNING,
                    message: this.$t('dynamicess.waitingForRealtime', {
                        i18nFallback:
                            'Make sure you have two-way communication enabled in order to turn off dynamic ess.',
                    }),
                });
                return;
            }
        }
        const installation = await this.InstallationsService.getInstallation(this.idSite);
        this.MQTTRealtimeService.publishToPath(
            {
                identifier: installation.identifier,
                dbusServiceType: 'settings',
                dbusPath: '/Settings/DynamicEss/Mode',
                instance: 0,
                callbackIdentifier: 'realtimeDessMode',
                prefix: 'W/',
            },
            { value: +value },
        );

        this.ApiService.essSettings(this.idSite, { isOn: value }, true, true).then(res => {
            this.dynamicEssToggle = res.data.data.isOn;
            // api data present
            this.apiDynamicEssToggle =
                checkIfPresent(res.data.data.isOn) && checkIfPresent(res.data.data.buyPriceType);
            // map vuex form data
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FROM_API, res.data.data);
            this.$store.commit(DYNAMIC_ESS.SET_UP_COUNTRY_BASED_ON_BIDDING);
            this.$store.commit(DYNAMIC_ESS.SET_UP_PROVIDERS_MAPPING);
        });
    }

    resetWizard() {
        this.showWizard = false;
        this.currentId = STEPS.GRID;
    }

    get completedUntil() {
        let stepId = this.currentId;
        for (const step of this.getSteps) {
            const isValid = step.validator ? this.$store.getters[step.validator] : true;
            if (!isValid) {
                break;
            }
            stepId = step.id;
        }
        return this.getSteps.findIndex(i => i.id === stepId) + 1;
    }

    get isConfigurationEmpty() {
        // If all the GRID Step fields are null, the configuration was not started
        return (
            this.$store.state.dynamicEssForm.energyGridSell == null &&
            this.$store.state.dynamicEssForm.maximumImportPower == null &&
            this.$store.state.dynamicEssForm.maximumExportPower == null &&
            this.$store.state.dynamicEssForm.isGreenModeOn == null
        );
    }

    setCurrentId(newId) {
        for (const step of this.getSteps) {
            const isValid = step.validator ? this.$store.getters[step.validator] : true;
            if (step.id === newId) {
                this.currentId = newId;
                return;
            }
            if (!isValid) {
                return;
            }
        }
    }

    get getSteps() {
        return [
            {
                id: STEPS.GRID,
                title: this.$t('dynamicess.wizard.grid', { i18nFallback: 'System' }),
                validator: DYNAMIC_ESS.GRID_VALID,
            },
            {
                id: STEPS.BATTERY,
                validator: DYNAMIC_ESS.BATTERY_VALID,
                title: this.$t('dynamicess.wizard.battery', { i18nFallback: 'Battery' }),
            },
            {
                id: STEPS.ENERGY_PRICES_BUY,
                validator: DYNAMIC_ESS.BUY_VALID,
                title: this.$t('dynamicess.wizard.buyPrices', { i18nFallback: 'Buy prices' }),
            },
            this.$store.state.dynamicEssForm.energyGridSell && {
                id: STEPS.ENERGY_PRICES_SELL,
                validator: DYNAMIC_ESS.SELL_VALID,
                title: this.$t('dynamicess.wizard.sellPrices', { i18nFallback: 'Sell prices' }),
            },
            {
                id: STEPS.CONFIRMATION,
                title: this.$t('dynamicess.wizard.confirmation', { i18nFallback: 'Confirmation' }),
            },
        ].filter(Boolean);
    }

    // we need to check if the sell step is valid to show the user submit step or next step
    // when he already has submitted the form once
    get isSellValid() {
        if (this.creating) {
            return false;
        }
        return this.$store.state.dynamicEssForm.energyGridSell
            ? !!this.$store.getters[DYNAMIC_ESS.SELL_VALID]
            : true;
    }

    isStepValid() {
        if (this.currentId == STEPS.GRID) {
            return !this.$store.getters[DYNAMIC_ESS.GRID_VALID];
        }
        if (this.currentId == STEPS.BATTERY) {
            return !this.$store.getters[DYNAMIC_ESS.BATTERY_VALID](
                this.$store.getters[CURRENCIES.GET_ACTIVE].code,
            );
        }
        if (this.currentId == STEPS.ENERGY_PRICES_BUY) {
            return !this.$store.getters[DYNAMIC_ESS.BUY_VALID];
        }
        if (this.currentId == STEPS.ENERGY_PRICES_SELL) {
            return !this.$store.getters[DYNAMIC_ESS.SELL_VALID];
        }
        return false;
    }

    get idSite() {
        return this.$route.params.id;
    }

    editStep(step) {
        this.showWizard = true;
        this.isEdit = true;
        this.currentId = step;
    }

    mounted() {
        this.$rootScope.$on('installations:updated', this.onInstallationsUpdated);

        this.ApiService.call('/bidding-zones', {}, 'GET', false, 'json', false).then(response => {
            this.$store.commit(DYNAMIC_ESS.FILL_ZONES, response.data.records);
            this.$store.commit(DYNAMIC_ESS.SET_UP_COUNTRY_BASED_ON_BIDDING);
        });
        this.ApiService.call('/energy-providers', {}, 'GET', false, 'json', false).then(
            response => {
                this.$store.commit(DYNAMIC_ESS.FILL_PROVIDERS, response.data.records);
                this.$store.commit(DYNAMIC_ESS.SET_UP_PROVIDERS_MAPPING);
            },
        );
        this.$store.commit(CURRENCIES.SET_ACTIVE, { idCurrency: -1, code: '', name: '', sign: '' });
    }

    destroyed() {
        this.MQTTRealtimeService.unsubScribeFromPath({
            identifier: this.installation.identifier,
            dbusServiceType: 'settings',
            dbusPath: '/Settings/DynamicEss/Mode',
            instance: 0,
            callbackIdentifier: 'realtimeDessMode',
        });
        this.MQTTRealtimeService.unsubscribe();
    }

    startConfig() {
        this.showWizard = true;
        // Only show the pop-up when the configuration is empty
        if (this.isConfigurationEmpty) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_INFO,
                message: this.$t('dynamicess.progress', {
                    i18nFallback: 'The progress will be saved automatically.',
                }),
            });
        }
    }

    @Watch('idSite', { immediate: true })
    async loadSiteData() {
        if (!this.idSite) return;

        const idBefore = this.idSite;
        const installation = await this.InstallationsService.getInstallation(this.idSite);
        // enable mqtt and set realtime installation
        // in case user disables the dynamic ess we have to turn off dess via mqtt
        this.InstallationsService.setRealtimeInstallation(installation);
        this.MQTTRealtimeService.subscribe();
        this.MQTTRealtimeService.subscribeToPath({
            identifier: installation.identifier,
            dbusServiceType: 'settings',
            dbusPath: '/Settings/DynamicEss/Mode',
            instance: 0,
            callback: message => {
                this.realtimeDessMode = message.payload.value;
            },
            callbackIdentifier: 'realtimeDessMode',
        });

        if (idBefore !== this.idSite) return;
        this.installation = installation;
        this.userCanEdit =
            this.installation.view_permissions.update_settings &&
            this.installation.view_permissions.dess_config;

        this.$store.commit(CURRENCIES.SET_ACTIVE, this.installation.currency);

        this.$store.commit(DYNAMIC_ESS.SET_CAPABILITIES, this.installation);

        this.ApiService.call2(
            '/installations/' + this.idSite + '/dynamic-ess-settings',
            {},
            { method: 'GET', skipResultChecks: true },
        ).then(response => {
            if (!response.data.success) {
                this.dynamicEssToggle = false;
                this.apiDynamicEssToggle = false;
                return;
            }
            this.dynamicEssToggle = response.data.data.isOn;
            // api data present
            this.apiDynamicEssToggle =
                checkIfPresent(response.data.data.isOn) &&
                checkIfPresent(response.data.data.buyPriceType);

            this.hasVenusConfigSupport = response.data.data.hasVenusConfigSupport;

            // map vuex form data
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FROM_API, response.data.data);
            this.$store.commit(DYNAMIC_ESS.SET_UP_COUNTRY_BASED_ON_BIDDING);
            this.$store.commit(DYNAMIC_ESS.SET_UP_PROVIDERS_MAPPING);

            applyData(this.initialForm, response.data.data);
        });
    }

    onSubmit({ closure }) {
        const data = this.$store.getters[DYNAMIC_ESS.TO_API_DTO];
        this.ApiService.essSettings(this.idSite, data, true, this.apiDynamicEssToggle).then(
            async () => {
                if (closure) {
                    this.closure = true;
                } else {
                    this.showWizard = false;
                }
                if (this.dynamicEssToggle) {
                    this.ApiService.scheduleDynamicEss(this.idSite);
                }

                const installation = await this.InstallationsService.getInstallation(this.idSite);
                if (this.realtimeDessMode === null) {
                    this.vrmNotificationsService.add({
                        type: NOTIFICATION_TYPE_WARNING,
                        message: this.$t('dynamicess.noRealtimeDessOn', {
                            i18nFallback:
                                "Dynamic ESS can't be turned on because two-way communication is not enabled. Please enable two-way communication and enable it from the controls",
                        }),
                    });
                } else {
                    this.MQTTRealtimeService.publishToPath(
                        {
                            identifier: installation.identifier,
                            dbusServiceType: 'settings',
                            dbusPath: '/Settings/DynamicEss/BatteryCapacity',
                            instance: 0,
                            callbackIdentifier: 'realtimeDessMode',
                            prefix: 'W/',
                        },
                        { value: Number(data.batteryCapacity).toFixed(2) },
                    );
                    this.MQTTRealtimeService.publishToPath(
                        {
                            identifier: installation.identifier,
                            dbusServiceType: 'settings',
                            dbusPath: '/Settings/DynamicEss/Mode',
                            instance: 0,
                            callbackIdentifier: 'realtimeDessMode',
                            prefix: 'W/',
                        },
                        { value: 1 },
                    );

                    if (this.hasVenusConfigSupport) {
                        this.MQTTRealtimeService.publishToPath(
                            {
                                identifier: installation.identifier,
                                dbusServiceType: 'settings',
                                dbusPath: '/Settings/DynamicEss/OperatingMode',
                                instance: 0,
                                callbackIdentifier: 'realtimeDessMode',
                                prefix: 'W/',
                            },
                            { value: data.isGreenModeOn ? 1 : 0 },
                        );

                        this.MQTTRealtimeService.publishToPath(
                            {
                                identifier: installation.identifier,
                                dbusServiceType: 'settings',
                                dbusPath: '/Settings/DynamicEss/GridExportLimit',
                                instance: 0,
                                callbackIdentifier: 'realtimeDessMode',
                                prefix: 'W/',
                            },
                            { value: Number(data.maxPowerToGrid).toFixed(2) },
                        );
                        this.MQTTRealtimeService.publishToPath(
                            {
                                identifier: installation.identifier,
                                dbusServiceType: 'settings',
                                dbusPath: '/Settings/DynamicEss/GridImportLimit',
                                instance: 0,
                                callbackIdentifier: 'realtimeDessMode',
                                prefix: 'W/',
                            },
                            { value: Number(data.maxPowerFromGrid).toFixed(2) },
                        );
                        this.MQTTRealtimeService.publishToPath(
                            {
                                identifier: installation.identifier,
                                dbusServiceType: 'settings',
                                dbusPath: '/Settings/DynamicEss/BatteryChargeLimit',
                                instance: 0,
                                callbackIdentifier: 'realtimeDessMode',
                                prefix: 'W/',
                            },
                            { value: Number(data.chargePower).toFixed(2) },
                        );
                        this.MQTTRealtimeService.publishToPath(
                            {
                                identifier: installation.identifier,
                                dbusServiceType: 'settings',
                                dbusPath: '/Settings/DynamicEss/BatteryDischargeLimit',
                                instance: 0,
                                callbackIdentifier: 'realtimeDessMode',
                                prefix: 'W/',
                            },
                            { value: Number(data.dischargePower).toFixed(2) },
                        );
                    }
                }

                this.ApiService.call2(
                    '/installations/' + this.idSite + '/dynamic-ess-settings',
                    {},
                    { method: 'GET', skipResultChecks: true },
                ).then(response => {
                    if (response.data.success && response.data.data) {
                        applyData(this.initialForm, response.data.data);
                    }
                });
                this.hasChange = false;
                this.creating = false;
                this.formChanges.grid = false;
                this.formChanges.battery = false;
                this.formChanges.buyForm = false;
                this.formChanges.sellForm = false;
            },
        );
    }

    handleChange(value) {
        this.formChanges[value.formName] = value.hasChange;
        this.hasChange =
            this.formChanges.grid ||
            this.formChanges.battery ||
            this.formChanges.buyForm ||
            this.formChanges.sellForm;
    }

    handleCreate(value) {
        this.creating = value;
        this.hasChange = value;
    }

    onResetForm() {
        this.loadSiteData();
        this.hasChange = false;
        this.creating = false;
        this.formChanges.grid = false;
        this.formChanges.battery = false;
        this.formChanges.buyForm = false;
        this.formChanges.sellForm = false;
    }

    async onInstallationsUpdated() {
        this.installation = await this.InstallationsService.getInstallation(this.idSite);
    }

    submitAndReset(closure) {
        this.onSubmit(closure);
        this.resetWizard();
    }
}
</script>
